import { Link } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { Button } from '@/components/ui/button'
import { esEntornoNoProductivo } from '@/lib/entorno'
import { Shield, Zap, Users, LogIn } from 'lucide-react'

const isNonProd = esEntornoNoProductivo()

export function Login() {
  const { loginWithRedirect, isLoading: authLoading } = useAuth0()

  const handleLogin = () => {
    loginWithRedirect({ appState: { returnTo: '/' } })
  }

  return (
    <div className="min-h-screen flex bg-gray-50 relative">
      {isNonProd && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-amber-950 text-center text-xs font-semibold py-0.5 tracking-wide">
          ENTORNO DE DESARROLLO
        </div>
      )}
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-emerald-700 p-12 flex-col justify-between relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 bg-grid-white/10 [mask-image:linear-gradient(0deg,transparent,white)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-700/50 to-transparent" />

        <div className="relative z-10">
          {/* Logo */}
          <div className="mb-8">
            <img
              src="/menta-logo.svg"
              alt="Menta"
              className="h-19"
            />
            <p className="text-emerald-100 text-sm mt-2 ml-1">
              El punto desde donde tu estudio avanza.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-6 mt-16">
            <div className="flex items-start gap-4 text-white">
              <div className="p-2 bg-white/10 rounded-lg mt-1">
                <Shield className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Seguro y Confiable</h3>
                <p className="text-emerald-100 text-sm">
                  Autenticación de nivel empresarial con Auth0 y protección de datos
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-white">
              <div className="p-2 bg-white/10 rounded-lg mt-1">
                <Zap className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Rápido y Eficiente</h3>
                <p className="text-emerald-100 text-sm">
                  Gestiona clientes, movimientos y reportes en tiempo real
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 text-white">
              <div className="p-2 bg-white/10 rounded-lg mt-1">
                <Users className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Multi-tenant</h3>
                <p className="text-emerald-100 text-sm">
                  Aislamiento completo de datos por organización
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-emerald-100 text-sm">
          <p>&copy; {new Date().getFullYear()} Menta. Todos los derechos reservados.</p>
        </div>
      </div>

      {/* Right Panel - Login */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-10">
            <img
              src="/menta-logo-dark.svg"
              alt="Menta"
              className="h-19"
            />
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 space-y-8">
            {/* Header */}
            <div className="text-center space-y-3">
              <h2 className="text-3xl font-bold text-emerald-700">
                Bienvenido de nuevo
              </h2>
              <p className="text-gray-600">
                Accede a tu cuenta para gestionar tu organización
              </p>
            </div>

            {/* Login Button */}
            <Button
              onClick={handleLogin}
              disabled={authLoading}
              className="w-full h-14 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed rounded-xl"
            >
              {authLoading ? (
                <>
                  <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3" />
                  Conectando...
                </>
              ) : (
                <>
                  <LogIn className="h-5 w-5 mr-3" />
                  Acceder
                </>
              )}
            </Button>

            {/* Terms */}
            <p className="text-center text-xs text-gray-400">
              Al acceder, aceptas nuestros{' '}
              <Link to="/terminos" className="text-emerald-600 hover:underline">
                términos de servicio
              </Link>{' '}
              y{' '}
              <Link to="/privacidad" className="text-emerald-600 hover:underline">
                política de privacidad
              </Link>
            </p>
          </div>

          {/* Footer Help */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              ¿Necesitas ayuda?{' '}
              <Link
                to="/soporte"
                className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
              >
                Contacta con soporte
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
